import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { confirmationAlert } from '../../../utility/SweetAlert'
import { t } from 'i18next'
import moment from 'moment'
import InteractorShippingHeaderApproval from '../../../interactors/transaction/shipping/InteractorShippingHeaderApproval'
import Modal from 'react-responsive-modal'
import { formatDate, currencyFormatDecimal } from '../../../utility/Helpers'
import { useSelector } from 'react-redux'
import InteractorShippingHeaderCancel from '../../../interactors/transaction/shipping/InteractorShippingHeaderCancel'
import { InteractorOrderSearch } from '../../../interactors/Main'
import { useLocation } from 'react-router-dom'

const headers = [
  { label: t('title.shipping_date_sent'), key: 'shipping_date_sent' },
  { label: t('title.shipping_code'), key: 'shipping_code' },
  { label: t('title.shipping_number'), key: 'shipping_number' },
  { label: t('title.status'), key: 'initial_status' },
  { label: t('title.action'), key: 'actions' },
]

const detailDataHeaders = [
  { label: t('title.product.title') },
  { label: t('title.product.qty') },
  { label: t('title.product.product_price') },
  { label: t('title.subtotal_product') },
]

const renderStatus = (status) => {
  const base = 'badge rounded-pill px-3 py-1 fw-medium'
  switch (status) {
    case 'SHIPPED':
      return <span className={`${base} bg-warning text-dark`}>{t('status.bill.SHIPPED')}</span>
    case 'DELIVERED':
      return <span className={`${base} bg-success text-white`}>{t('status.bill.DELIVERED')}</span>
    case 'CANCELLED':
      return <span className={`${base} bg-danger text-white`}>{t('status.bill.CANCELLED')}</span>
    case 'CORRECTION':
      return (
        <span className={`${base} bg-secondary text-white`}>{t('status.bill.CORRECTION')}</span>
      )
    default:
      return <span className={`${base} bg-secondary text-white`}>{status}</span>
  }
}

const OrderShippingTable = (dataItems) => {
  const { t } = useTranslation()
  const currTime = moment()
  const location = useLocation()
  const { data } = dataItems
  const { datacard } = data
  const { items } = datacard
  const { account } = useSelector((state) => state.account)
  const [detailShipping, setDetailShipping] = useState({})
  const [show, setShow] = useState(false)
  const { shippingHeaderApproval } = InteractorShippingHeaderApproval()
  const { shippingHeaderCancel } = InteractorShippingHeaderCancel()
  const { orderSearch } = InteractorOrderSearch()
  const [orderData, setOrderData] = useState([])
  const [loading, setLoading] = useState(true)
  const [payload] = useState({
    page: 1,
    limit: 5,
    keywords: {
      text_search: '',
      status: '',
      payment_type: '',
      account_uid: account.uid,
      store_uid: '',
      uid: location.pathname.split('/')[3],
    },
  })

  const confirmationShippingStatus = async (e, data, type) => {
    e.preventDefault()
    const payload = {
      uid: data.uid,
      status: type,
    }
    const payloadUpdate =
      type === 'DELIVERED'
        ? {
            ...payload,
            shipping_date_delivered: currTime.format('YYYY-MM-DD HH:mm:ss'),
          }
        : {
            ...payload,
            updated_by: account.full_name,
          }
    const confirm = await confirmationAlert({
      title: t('title.are_you_sure'),
      message:
        type === 'DELIVERED'
          ? t('message.confirmation_delivered_shipped')
          : t('message.confirmation_delivered_cancelled'),
      icon: 'warning',
    })
    if (confirm) {
      type === 'DELIVERED'
        ? shippingHeaderApproval(payloadUpdate)
        : shippingHeaderCancel(payloadUpdate)
    }
  }

  const showModal = () => {
    setShow((prev) => !prev)
  }

  const showDetail = (row) => {
    setDetailShipping(row)
    showModal()
  }

  const handleResponse = useCallback((data) => {
    setOrderData(data)
  }, [])

  const formatDateOnly = (date) => (date ? formatDate(date, 'YYYY-MM-DD') : '-')

  useEffect(() => {
    if (loading) {
      orderSearch(payload, handleResponse, setLoading)
    }
  }, [handleResponse, loading, payload, orderSearch])

  return (
    <>
      <div className="w-100 px-3 py-5">
        <div className="d-flex justify-content-center">
          <div className="table-responsive table-hover bg-white rounded shadow-sm w-100">
            <table className="table align-middle mb-0 w-100">
              <thead className="bg-light text-muted text-uppercase small">
                <tr>
                  {headers.map((header, i) => {
                    const isFirst = i === 0
                    const isLast = i === headers.length - 1
                    return (
                      <th
                        key={header.key}
                        className={`py-2 px-3 fw-semibold text-black border-top border-bottom ${
                          !isLast && header.key === 'initial_status' ? 'w-20' : ''
                        } ${isFirst ? 'border-start' : ''} ${isLast ? 'border-end w-35' : ''}`}
                      >
                        {header.label}
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {items ? (
                  items.map((row, index) => (
                    <tr
                      key={index}
                      className="border-bottom text-dark"
                      style={{ fontSize: '0.875rem', fontWeight: 400 }}
                    >
                      {headers.map(({ key }) => (
                        <td key={key} className="py-2 align-middle">
                          {key === 'initial_status' ? (
                            <>
                              {renderStatus(row[key])}
                              {row[key] === 'DELIVERED' && row['shipping_date_delivered'] && (
                                <div className="before-value-text-smaller mt-1">
                                  {t('title.shipping_date_delivered')} : <br />
                                  {moment(row['shipping_date_delivered']).format(
                                    'DD MMMM YYYY HH:mm',
                                  )}
                                </div>
                              )}
                            </>
                          ) : key === 'actions' ? (
                            <div className="d-flex flex-wrap justify-content-start gap-2 w-100">
                              <button
                                type="button"
                                className="d-inline btn rounded-pill btn-primary btn-xs my-1 mx-1 font-size-10"
                                onClick={() => showDetail(row)}
                              >
                                <i className="fa fa-eye mr-1"></i>
                                {t('button.view')}
                              </button>
                              {row.shipping_date_sent &&
                                (currTime.isAfter(moment(row.shipping_date_sent)) ||
                                  currTime.isSame(moment(row.shipping_date_sent))) &&
                                row.initial_status === 'SHIPPED' &&
                                orderData?.items?.[0]?.status !== 'CLOSE_ORDER_REQUEST' && (
                                  <button
                                    type="button"
                                    onClick={(e) => confirmationShippingStatus(e, row, 'DELIVERED')}
                                    className="d-inline btn rounded-pill btn-success pointer btn-xs my-1 mx-1 font-size-10"
                                  >
                                    <i className="fa fa-check mr-1"></i>{' '}
                                    {t('button.received_order')}
                                  </button>
                                )}
                              {row.initial_status === 'SHIPPED' &&
                                orderData?.items?.[0]?.status !== 'CLOSE_ORDER_REQUEST' && (
                                  <button
                                    type="button"
                                    onClick={(e) => confirmationShippingStatus(e, row, 'CANCELLED')}
                                    className="d-inline btn rounded-pill btn-danger pointer btn-xs my-1 mx-1 font-size-10"
                                  >
                                    <i className="fa fa-times mr-1"></i> {t('button.cancelled')}
                                  </button>
                                )}
                            </div>
                          ) : (
                            <>
                              {key === 'shipping_date_sent'
                                ? formatDateOnly(row[key])
                                : row[key] || '-'}
                            </>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr
                    className="border-bottom text-dark"
                    style={{ fontSize: '0.875rem', fontWeight: 400 }}
                  >
                    <td colSpan={headers.length} className="text-center">
                      {t('title.no_data')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Modal open={show} onClose={showModal}>
        <div className="container-fluid mx-0">
          <div className="row justify-content-center py-2  mb-4">
            <h5 className="font-weight-bold">{t('title.detail_order')}</h5>
          </div>
          <div className="row align-items-baseline">
            <div className="col-6 mb-3">
              <div>
                <small>{t('title.shipping_date_sent')}</small> <br />
                <strong>
                  {detailShipping?.shipping_date_sent ? detailShipping.shipping_date_sent : '-'}
                </strong>
              </div>
            </div>
            {detailShipping?.shipping_date_delivered && (
              <div className="col-6 mb-3">
                <div>
                  <small>{t('title.shipping_date_delivered')}</small> <br />
                  <strong>{detailShipping.shipping_date_delivered}</strong>
                </div>
              </div>
            )}
            <div className="col-6 mb-3">
              <div>
                <small>{t('title.bill_status')}</small> <br />
                <strong>
                  {detailShipping?.bill_status
                    ? t(`status.shipping_bill_status.${detailShipping.bill_status.toLowerCase()}`)
                    : '-'}
                </strong>
              </div>
            </div>
            <div className="col-6 mb-3">
              <div>
                <small>{t('title.shipping_code')}</small> <br />
                <strong>
                  {detailShipping?.shipping_code ? detailShipping.shipping_code : '-'}
                </strong>
              </div>
            </div>
            <div className="col-6 mb-3">
              <div>
                <small>{t('title.shipping_number')}</small> <br />
                <strong>
                  {detailShipping?.shipping_number ? detailShipping.shipping_number : '-'}
                </strong>
              </div>
            </div>
            <div className="col-6 mb-3">
              <div>
                <small>{t('title.status')}</small> <br />
                {renderStatus(detailShipping?.initial_status)}
              </div>
            </div>
            <div className="col-12">
              <div>
                <small>{t('title.note')}</small> <br />
                <strong style={{ overflowWrap: 'break-word' }}>
                  {detailShipping?.shipping_note ? detailShipping.shipping_note : '-'}
                </strong>
              </div>
            </div>
            <div className="col-12 my-3">
              <hr className="m-0" />
            </div>
            <div className="col-12">
              <h6 className="mb-2">
                <strong>{t('title.delivery_product')}</strong>
              </h6>
              <div className="card p-3 mt-2">
                <div className="table-responsive">
                  <table className="table align-middle mb-0 w-100">
                    <thead className="bg-light text-muted text-uppercase small">
                      <tr>
                        {detailDataHeaders.map((header, i) => (
                          <th key={i} className="py-2 px-3 fw-semibold text-black">
                            {header.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {detailShipping.shipping_items?.length > 0 ? (
                        detailShipping.shipping_items.map((item, index) => {
                          const subTotal = item.product_price_item * item.product_quantity || 0
                          return (
                            <tr key={index}>
                              <td>
                                <div
                                  className="badge bg-primary text-white rounded-pill px-2 py-1 fw-bold"
                                  style={{ fontSize: '9px' }}
                                >
                                  {t(`title.${item.product_type.toLowerCase()}`)}
                                </div>
                                <div>
                                  <b style={{ overflowWrap: 'anywhere' }}>{item.product_title}</b>
                                </div>
                              </td>
                              <td>
                                {item.product_quantity} {item.product_unit_type}
                              </td>
                              <td>
                                {currencyFormatDecimal(
                                  item.product_price_item,
                                  item.product_price_currency,
                                )}
                              </td>
                              <td>
                                <div>
                                  {currencyFormatDecimal(subTotal, item.product_price_currency)}
                                </div>
                                {item.product_type === 'SERVICE' && (
                                  <small style={{ fontSize: '90% !important' }}>
                                    <i>
                                      {t('title.ppn_amount')}:{' '}
                                      {currencyFormatDecimal(
                                        item.product_price_ppn,
                                        item.product_price_currency,
                                      )}
                                    </i>
                                  </small>
                                )}
                              </td>
                            </tr>
                          )
                        })
                      ) : (
                        <tr>
                          <td colSpan={4} className="text-center">
                            {t('title.no_data')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default OrderShippingTable
